// Betreiber-/SaaS-Domäne (Operator-Dashboard): Lizenz-Status + Preis-Wahrheit + KPI-Rechnung.
// Rein & I/O-frei (Schicht-Regel) — Backend aggregiert nur Rohzeilen, gerechnet wird hier (eine Wahrheit).
// Geld IMMER in ganzen Cent (Projekt-Verbot: nie Float-Euro).

/** Lizenz-Status einer Werkstatt. 'suspended' = vom Betreiber gesperrt (reversibel, z. B. Zahlungsverzug);
 *  'canceled' = gekündigt (terminal; Daten bleiben für GoBD-/DSGVO-Fristen, Zugang bleibt zu). Beide sperren
 *  Login UND laufende Sessions (auth.currentUser prüft je Request). */
export type WorkshopStatus = 'active' | 'suspended' | 'canceled'
export const WORKSHOP_STATUSES: readonly WorkshopStatus[] = ['active', 'suspended', 'canceled']

export function isWorkshopStatus(v: unknown): v is WorkshopStatus {
  return typeof v === 'string' && (WORKSHOP_STATUSES as readonly string[]).includes(v)
}

/**
 * Monatspreis je Tarif in CENT — die EINZIGE Preis-Wahrheit der App (Entscheidung 13.07.2026).
 * Startwerte an den Wettbewerb angelehnt (easyWerkstatt 25/49/99 €); zentral hier ändern, nie lokal.
 * test/probe (Testphasen) und owner (Betreiber-eigene Instanz) sind bewusst 0 → zählen nicht in MRR/ARPA.
 */
export const PLAN_PRICE_CENTS: Record<string, number> = {
  test: 0,
  probe: 0,
  owner: 0,
  solo: 2900,
  team: 4900,
  werkstatt: 9900,
}

/** Monatspreis eines Tarifs; unbekannter/fehlender Tarif → 0 (defensiv, nie werfen). */
export function planPriceCents(plan: string | null | undefined): number {
  return (plan != null ? PLAN_PRICE_CENTS[plan] : undefined) ?? 0
}

/** Minimale Werkstatt-Sicht für KPI-Rechnung (strukturtypisiert — DTOs passen ohne Mapping). */
export interface BillableWorkshop {
  plan: string | null
  status: WorkshopStatus
}

/** MRR in Cent: Σ Tarifpreis aller AKTIVEN Werkstätten (suspended/canceled zahlen nicht). */
export function mrrCents(workshops: readonly BillableWorkshop[]): number {
  return workshops.reduce((sum, w) => sum + (w.status === 'active' ? planPriceCents(w.plan) : 0), 0)
}

/** Zahlende Accounts: aktiv UND Tarifpreis > 0 (Test-/Probe-/Owner-Instanzen sind keine Kunden). */
export function payingCount(workshops: readonly BillableWorkshop[]): number {
  return workshops.filter((w) => w.status === 'active' && planPriceCents(w.plan) > 0).length
}

/** ARPA in Cent (Umsatz je zahlendem Account); keine zahlenden Accounts → 0 (nie NaN/÷0). */
export function arpaCents(workshops: readonly BillableWorkshop[]): number {
  const paying = payingCount(workshops)
  return paying === 0 ? 0 : Math.round(mrrCents(workshops) / paying)
}

/** MRR je Tarif (nur aktive) — fürs Tarif-Breakdown im KPI-Widget. Stabil sortiert nach MRR absteigend. */
export function mrrByPlan(workshops: readonly BillableWorkshop[]): { plan: string; count: number; mrrCents: number }[] {
  const acc = new Map<string, { count: number; mrrCents: number }>()
  for (const w of workshops) {
    if (w.status !== 'active') continue
    const plan = w.plan ?? 'test'
    const e = acc.get(plan) ?? { count: 0, mrrCents: 0 }
    e.count += 1
    e.mrrCents += planPriceCents(plan)
    acc.set(plan, e)
  }
  return [...acc.entries()]
    .map(([plan, e]) => ({ plan, ...e }))
    .sort((a, b) => b.mrrCents - a.mrrCents || a.plan.localeCompare(b.plan))
}

/** Ab so vielen Tagen ohne Login gilt eine aktive Werkstatt als Churn-Risiko (Betreiber-Warnchip). */
export const INACTIVITY_WARN_DAYS = 14
